import * as React from 'react';

import { cn } from '../../lib/utils';

interface SwitchProps extends Omit<React.ComponentPropsWithoutRef<'button'>, 'onChange' | 'type'> {
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

/**
 * The 40×23 toggle the design draws, as a real `<button role="switch" aria-checked>`: the role
 * and the state are what a screen reader announces, and a button already activates on Enter and
 * Space, which a styled `<div>` would have to re-implement. Hand-rolled because radix is not a
 * declared dependency of this project.
 */
const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ className, checked, onCheckedChange, onClick, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={(event) => {
        onClick?.(event);
        onCheckedChange?.(!checked);
      }}
      className={cn(
        'flex h-[23px] w-10 shrink-0 items-center rounded-full p-[2px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus-visible:ring-zinc-300',
        checked ? 'justify-end bg-zinc-900 dark:bg-zinc-200' : 'justify-start bg-zinc-300 dark:bg-zinc-700',
        className
      )}
      {...props}
    >
      <span
        className="h-[19px] w-[19px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.25)] dark:bg-zinc-950"
        aria-hidden="true"
      />
    </button>
  )
);
Switch.displayName = 'Switch';

export { Switch };
